// React imports
import React, { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Tooltip from '@mui/material/Tooltip'
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress';
import { green, red } from '@mui/material/colors';

// FontAwesome imports
import { faCheck, faTimes, faUpload, faDiagramVenn, faCoins, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Local imports
import Header from '../../Header.js';
import { formulaToParams, lclWMFormulaToText } from '../../utils/Formula.js';
import { getMultiplier } from '../../CalcWeight.js';
import currencyToText from '../../CurrencyToText.js';
import { getOverlappingRates, getRatesFromServer } from '../utils/rates.js';
import { convertMonadaCsv } from './utils.js';

export default function RatesUpload() {
    const { me, org, unlocode, seaPorts, accesstoken } = useOutletContext();
    const [ step, setStep ] = useState(1);
    const [ lastChecked, setLastChecked ] = useState(0);
    const [ successful, setSuccesful ] = useState([]);
    const [ failed, setFailed ] = useState([]);
    const [ omit, setOmit ] = useState([]);
    const [ uploaded, setUploaded ] = useState([]);
    const [ uploadedFailed, setUploadedFailed ] = useState([]);
    const [ uploading, setUploading ] = useState(false);
    const [ changeValidFrom, setChangeValidFrom ]  = useState(true);
    const [ changeValidUntil, setChangeValidUntil ]  = useState(true);
    const [ linking, setLinking ] = useState(false);
    const [ linked, setLinked ] = useState(null);
    const [ token, setToken ] = useState(null);
    const [ show, setShow ] = useState('all');
    const [ loadingFile, setLoadingFile ] = useState(false);
    // Kept so the published CSV can be archived alongside its raw file (see archiveImport).
    const [ csvText, setCsvText ] = useState(null);
    const [ csvName, setCsvName ] = useState(null);
    const [ uploadingFailed, setUploadingFailed ] = useState(false);
    const navigate = useNavigate();

    async function onUpload() {
        setUploading(true);

        let newRates = [];

        const uploading = [];

        for (let i = 0 ; i < successful.length ; i++) {
            const index = i;
            let rate = successful[index];

            if (changeValidFrom) {
                const now = dayjs();
                rate.offer.validFrom = now.format('YYYY-MM-DD');
            }

            if (linked) {
                rate.attributes = rate.attributes || {};
                rate.attributes.source = linked;
            }

            if (!omit.includes(index)) {
                uploading.push(rate);
            }
        }

        try {
            let allSuccessful = [];
            let allFailedRates = [];
            // Split uploading into chunks of 1000
            const CHUNK_SIZE = 100;
            for (let i = 0; i < uploading.length; i += CHUNK_SIZE) {
                const chunk = uploading.slice(i, i + CHUNK_SIZE);
                const { data: results } = await axios.post(
                    `/api/rates/bulk?accesstoken=${accesstoken}&organizationId=${org.id}`,
                    { rates: chunk }
                );
                allSuccessful = allSuccessful.concat(results.successful);
                allFailedRates = allFailedRates.concat(results.failed || []);
            }
            newRates = allSuccessful.map(s => s.rate);
            setUploadedFailed(allFailedRates.map(f => ({ index: f.index, reason: f.error, ...f })));
        } catch (e) {
            console.error('Error uploading rates', e);
            setUploadingFailed(true);
            await new Promise(res => setTimeout(res, 2000));
            setUploadingFailed(false);
            setUploading(false);
            return;
        }

        const rates = await getRatesFromServer({ accesstoken, organizationId: org.id, since: 0 });
        const overlapping = getOverlappingRates({ rates });

        if (changeValidUntil) {

            const updatedRates = [];
            
            newRates.forEach(newRate => {
                const group = overlapping.find(ov => ov.map(o => o.id).includes(newRate.id));
                const maxValidUntil = new Date(`${newRate.offer.validFrom} 12:00`);
                (group || []).forEach(r => {
                    if (r.id === newRate.id) return;
                    const validUntil = new Date(`${r.offer.validUntil} 12:00`);

                    if (validUntil.getTime() >= maxValidUntil.getTime()) {
                        r.offer.validUntil = dayjs(maxValidUntil.getTime() - 24 * 60 * 60 * 1000).format('YYYY-MM-DD')
                        updatedRates.push(r);
                    }
                });
            });

            for (let i = 0 ; i < updatedRates.length ; i++) {
                const edit = updatedRates[i];
                await axios.put(`/api/rates/${edit.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { rate: _.omit(edit, ['id']) });
            }
        }

        const str = JSON.stringify({
            organizationId: org.id,
            form: successful[0].formId,
            newRates: newRates.map(nr => ({ id: nr.id, product: nr.product.type, source: nr.source?.text || 'everywhere', destination: nr.destination?.text || 'everywhere' }))
        });

        await archiveImport({ rateCount: newRates.length });

        setToken(str);
        setUploading(false);
    }

    function onOpenToken() {
        const win = window.open('', 'Title', 'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes');
        win.document.body.innerHTML = '<pre>' + token + '</pre>';
    }

    async function onLoadFile() {

        setLoadingFile(true);

        await new Promise(res => setTimeout(res, 200));

        const fileToLoad = document.getElementById('fileToLoad').files[0];

        const fileReader = new FileReader();
        fileReader.onload = async function(fileLoadedEvent){
            const textFromFileLoaded = fileLoadedEvent.target.result;
            setCsvText(textFromFileLoaded);
            setCsvName(fileToLoad.name);
            const parsed = await convertMonadaCsv({ data: textFromFileLoaded, org, unlocode, seaPorts });
            const _successful = parsed.successful.map(r => r.rate);
            if (_.some(_successful, s => s.formId !== _successful[0].formId)) {
                parsed.failed = [...parsed.failed, ..._successful];
                parsed.successful = [];
            }
            setSuccesful(parsed.successful.map(r => r.rate));
            setFailed(parsed.failed);
            setOmit([]);
            setStep(2);
            setLoadingFile(false);
        };

        fileReader.readAsText(fileToLoad, 'UTF-8');
    }

    // Keeps the published CSV and the raw supplier file together for the admin Rate
    // Imports list, which is the corpus the rate-parsing agent learns from. Best effort:
    // the rates are already published by the time this runs, so a failure here must not
    // surface as an upload error.
    async function archiveImport({ rateCount }) {
        if (!csvText) return;
        try {
            const formData = new FormData();
            formData.append('file', new Blob([csvText], { type: 'text/csv' }), csvName || 'rates.csv');
            if (linked) formData.append('rawFile', linked);
            if (rateCount) formData.append('rateCount', String(rateCount));
            const first = successful[0];
            if (first?.supplier?.organization) formData.append('supplier', first.supplier.organization);
            if (first?.transportationMethod) formData.append('transportationMethod', first.transportationMethod);
            if (first?.offer?.validFrom) formData.append('validFrom', first.offer.validFrom);
            if (first?.offer?.validUntil) formData.append('validUntil', first.offer.validUntil);
            await axios.post(`/api/rates/import/admin-upload?accesstoken=${accesstoken}&organizationId=${org.id}`, formData);
        } catch (e) {
            console.error('[RatesUpload] archiving the import failed (rates were still published)', e);
        }
    }

    async function onLinkFile() {
        const formData = new FormData();
        const file = document.getElementById('fileToLink').files[0];
        if (!file) return;
        formData.append('file', file, file.name);
        setLinking(true);
        const { data } = await axios({
            url: `/api/opportunities/upload_file?accesstoken=${accesstoken}&organizationId=${org.id}`,
            method: 'post',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        setLinking(false);
        setLinked(data.location);
    }

    if (!seaPorts || !unlocode) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box>
            <Header
                icon={faCoins}
                title={'Rates'}
                tabs={_.compact([{
                    icon: faMagnifyingGlass,
                    title: 'Search',
                    action: () => navigate('../rates'),
                }, {
                    icon: faUpload,
                    title: 'Import',
                    action: () => navigate('../rates/import'),
                }, me.email !== 'admin@monada.ai' ? null : {
                    icon: faUpload,
                    title: 'Upload',
                    action: () => navigate('../rates/upload'),
                    selected: true
                }, {
                    icon: faDiagramVenn,
                    title: 'Overlapping',
                    action: () => navigate('../rates/overlapping')
                }])}
            />
            {step === 1 && (
                <Box sx={{ p: 3, pt: 0 }}>
                    <Box>
                        Select a File to Load:
                    </Box>
                    <Box sx={{ pt: 3, pb: 3 }}>
                        <input type='file' id='fileToLoad' />
                    </Box>
                    <Button fullWidth variant='contained' onClick={onLoadFile} disabled={loadingFile}>
                        {loadingFile ? <CircularProgress size={20} /> : 'Load File'}
                    </Button>
                </Box>
            )}
            {step === 2 && (
                <Box sx={{ p: 3, pt: 0 }}>
                    <Box sx={{ pb: 3 }}>
                        <div>
                            <FormControl>
                                <FormControlLabel 
                                    control={
                                        <Checkbox
                                            sx={{ margin: 'auto' }} 
                                            checked={changeValidFrom}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setChangeValidFrom(!changeValidFrom);
                                            }}
                                        />
                                    } 
                                    label={<Typography variant='body2'>Change the rates "valid from date" to be today</Typography>}
                                />
                            </FormControl>
                        </div>
                        <div>
                            <FormControl>
                                <FormControlLabel 
                                    control={
                                        <Checkbox
                                            sx={{ margin: 'auto' }} 
                                            checked={changeValidUntil}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setChangeValidUntil(!changeValidUntil);
                                            }}
                                        />
                                    } 
                                    label={<Typography variant='body2'>Change any overlapping rates "valid until date" to the "valid from date" of the new rates</Typography>}
                                />
                            </FormControl>
                        </div>
                    </Box>
                    <Box sx={{ pb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box sx={{ pb: 3 }}>
                            <Typography variant='body2' sx={{ textWrap: 'nowrap' }}>
                                Select a File to Link as a source:
                            </Typography>
                            <input type='file' id='fileToLink' />
                        </Box>
                        <Button sx={{ textWrap: 'nowrap' }} variant='contained' onClick={onLinkFile} disabled={linking || linked}>
                            Upload & Use
                        </Button>
                        {linking && (
                            <Box sx={{ display: 'flex', padding: '12px' }}>
                                <CircularProgress size={12} />
                            </Box>
                        )}
                        {linked && (
                            <Box sx={{ display: 'flex', padding: '12px' }}>
                                <a href={linked} target='_blank' rel='noopener noreferrer'>Linked</a>
                            </Box>
                        )}
                    </Box>
                    <Select value={show} onChange={(e) => setShow(e.target.value)} sx={{ marginBottom: '12px' }}>
                        <MenuItem value='all'>Show All</MenuItem>
                        <MenuItem value='failed'>Show Only Failed</MenuItem>
                    </Select>
                    <Button 
                        fullWidth 
                        variant='contained' 
                        onClick={onUpload} 
                        disabled={uploadingFailed || uploading || loadingFile}
                        sx={{ height: '48px', backgroundColor: uploadingFailed ? `${red[500]} !important` : '', color: uploadingFailed ? 'white !important' : '' }}
                    >
                        {uploadingFailed ? <FontAwesomeIcon icon={faTimes} /> : uploading ? <CircularProgress size={20} /> : 'Upload Selected'}
                    </Button>
                    <TableContainer sx={{ pt: 1 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 80 }}></TableCell>
                                    <TableCell>type</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Destination</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Valid</TableCell>
                                    <TableCell>Transshipment</TableCell>
                                    <TableCell>Values</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {failed.map((s, i) => (
                                    <TableRow key={`${s.source || 'everywhere'}-${s.destination || 'everywhere'}-${s.product}-${s.dangerous ? 'Y' : 'N'}-${i}`}>
                                        <TableCell sx={{ width: 80, textAlign: 'center' }}>
                                            <Tooltip title={'Failed parsing record'}>
                                                <IconButton>
                                                    <FontAwesomeIcon style={{ color: red[500] }} icon={faTimes} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{s.type}</TableCell>
                                        <TableCell>{s.source || 'Everywhere'}</TableCell>
                                        <TableCell>{s.destination || 'Everywhere'}</TableCell>
                                        <TableCell>{
                                            (s?.product?.type === 'LCL' || s?.product?.type === 'By Volume or Weight (sea)') ?
                                                `Per ${getMultiplier({ product: s.product, transportationMethod: s.transportationMethod, allProducts: [s.product], shorten: true, round: org.airRoundChargeableWeight }).multiplier} ${getMultiplier({ product: s.product, transportationMethod: s.transportationMethod, allProducts: [s.product], shorten: true, round: org.airRoundChargeableWeight }).multiplierText}${s.product.dangerous ? ' (Dangerous)' : ''}` :
                                            s?.product?.type
                                        }</TableCell>
                                        <TableCell>{s?.offer?.validFrom} - {s?.offer?.validUntil}</TableCell>
                                        <TableCell>{s?.offer?.transshipment}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                ))}
                                {successful.filter((s, i) => show === 'all' || (show === 'failed' && uploadedFailed.find(u => u.index === i))).map((s, i) => (
                                    <TableRow key={`${s.source?.text || 'everywhere'}-${s.destination?.text || 'everywhere'}-${s.product}-${s.dangerous ? 'Y' : 'N'}-${i}`}>
                                        <TableCell sx={{ width: 80, textAlign: 'center' }}>
                                            {uploaded.includes(i) && (
                                                <FontAwesomeIcon style={{ color: green[500] }} icon={faCheck} />
                                            )}
                                            {uploadedFailed.find(u => u.index === i) && (
                                                <Tooltip title={uploadedFailed.find(u => u.index === i).reason}>
                                                    <IconButton>
                                                        <FontAwesomeIcon style={{ color: red[500] }} icon={faTimes} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {uploading && !uploaded.includes(i) && !uploadedFailed.find(u => u.index === i)  && (
                                                <CircularProgress size={12} sx={{ margin: 'auto' }} />
                                            )}
                                            {!uploading && !uploaded.includes(i) && !uploadedFailed.find(u => u.index === i) && (
                                                <FormGroup>
                                                    <Checkbox
                                                        sx={{ margin: 'auto' }} 
                                                        checked={!omit.includes(i)}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                            setOmit(_.xor(omit, event.nativeEvent.shiftKey ? _.range(lastChecked + 1, i + 1) : [i]));
                                                            setLastChecked(i);
                                                        }}
                                                    />
                                                </FormGroup>
                                            )}
                                        </TableCell>
                                        <TableCell>{s.type}</TableCell>
                                        <TableCell>{s.source?.text || 'Everywhere'}</TableCell>
                                        <TableCell>{s.destination?.text || 'Everywhere'}</TableCell>
                                        <TableCell>{
                                            (s.product.type === 'LCL' || s.product.type === 'By Volume or Weight (sea)') ?
                                                `Per ${getMultiplier({ product: s.product, transportationMethod: s.transportationMethod, allProducts: [s.product], shorten: true, round: org.airRoundChargeableWeight }).multiplier} ${getMultiplier({ product: s.product, transportationMethod: s.transportationMethod, allProducts: [s.product], shorten: true, round: org.airRoundChargeableWeight }).multiplierText}${s.product.dangerous ? ' (Dangerous)' : ''}` :
                                            s.product.type
                                        }</TableCell>
                                        <TableCell>{s.offer.validFrom} - {s.offer.validUntil}</TableCell>
                                        <TableCell>{s.offer.transshipment}</TableCell>
                                        <TableCell>
                                            {_.flatten(s.offer.sections.map(section => {
                                                return section.offers[0].fields.map(field => {
                                                    const { multiplierText = ''} = getMultiplier({ product: s.product, transportationMethod: s.transportationMethod, allProducts: [s.product], shorten: true, round: org.airRoundChargeableWeight });
                                                    const value = _.values(field.values)[0];
                                                    const str = (() => {
                                                        if (value.formula) {
                                                            const lclWMText = lclWMFormulaToText({ formula: value.formula, currency: value.currency });
                                                            if (lclWMText) return lclWMText;
                                                            const attempt = formulaToParams({ formula: value.formula });
                                                            if (!attempt) {
                                                                return 'Formula';
                                                            } else if (attempt.fixed) {
                                                                return `${currencyToText(value.currency, true, attempt.fixed.value)}${attempt.fixed.type === 'flat' ? ' flat' : `/${multiplierText}`}${attempt.minimum ? ` with a minimum of ${currencyToText(value.currency, true, attempt.minimum)}` : ''}`;
                                                            } else if (attempt.tiers) {
                                                                return `Minimum: ${currencyToText(value.currency, true, attempt.minimum)}. ` +
                                                                    attempt.tiers.map((t, i) => `${i === 0 ? 'First ' + t.step +' ' + multiplierText : '' + attempt.tiers[i-1].step + ' ' + multiplierText + ' - ' + (i === attempt.tiers.length - 1 ? '∞' : t.step + ' ' + multiplierText)}: ${currencyToText(value.currency, true, t.value)}${t.type === 'flat' ? ' flat' : '/' + multiplierText}`).join(', ');
                                                            } else {
                                                                return 'Formula';
                                                            }
                                                        }
                                                        return `${value.value}${value.currency ? ' ' + value.currency : ''}`;
                                                    })();
                                                    return `${field.title} (${str})`;
                                                }).join(' | ');
                                            })).join(' | ')}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Button 
                        fullWidth 
                        variant='contained' 
                        onClick={onUpload} 
                        disabled={uploadingFailed || uploading || loadingFile}
                        sx={{ height: '48px', backgroundColor: uploadingFailed ? `${red[500]} !important` : '', color: uploadingFailed ? 'white !important' : '' }}
                    >
                        {uploadingFailed ? <FontAwesomeIcon icon={faTimes} /> : uploading ? <CircularProgress size={20} /> : 'Upload Selected'}
                    </Button>
                    <Typography variant='body1' sx={{ margin: '12px 0px' }}>
                        Summary: (Should be copied to the import process)
                    </Typography>
                    {token && (
                        <Button fullWidth variant='contained' onClick={onOpenToken}>
                            Open Summary
                        </Button>
                    )}
                    {uploading && (
                        <Box sx={{ display: 'flex', padding: '12px' }}>
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    )
}

